import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Clock, Users, Star, Search, Filter, ArrowLeft } from 'lucide-react';
import { useSurveyTemplates } from '@/hooks/useSurveyTemplates';
import { SurveyTemplateViewer } from '@/components/survey/SurveyTemplateViewer';
import { isUUID } from '@/lib/slugUtils';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToolsData, useCategoriesData } from '@/hooks/useToolsData';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { SurveyTemplateListSEO } from '@/components/SEO';

const SUBTITLE_WORD_LIMIT = 24;

const truncateWords = (text: string, maxWords: number): string => {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text.trim();
  return words.slice(0, maxWords).join(' ') + '…';
};

const SurveyTemplates = () => {
  const navigate = useNavigate();
  const { lang, templateId: templateParam } = useParams<{ lang?: string; templateId?: string }>();
  const prefix = `/${lang || 'en'}`;
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Fetch templates first to resolve slug/ID
  const { data: templates = [], isLoading, error } = useSurveyTemplates();
  
  // Get the selected template for header display
  const selectedTemplate = selectedTemplateId ? templates.find(t => t.id === selectedTemplateId) : null;
  
  // Sync local state when route changes
  useEffect(() => {
    if (templateParam && templates.length > 0) {
      const isId = isUUID(templateParam);
      const template = templates.find(t => 
        isId ? t.id === templateParam : (t as any).slug === templateParam
      );
      setSelectedTemplateId(template?.id || null);
    } else {
      setSelectedTemplateId(null);
    }
  }, [templateParam, templates]);

  const handleBack = () => {
    setSelectedTemplateId(null);
    navigate(`${prefix}/survey-templates`);
  };

  const handleViewTemplate = (template: any) => {
    setSelectedTemplateId(template.id);
    navigate(`${prefix}/survey-templates/${(template as any).slug || template.id}`);
  };

  const handleUseTemplate = (templateId: string) => {
    // TODO: hardcoded reference to Employee Engagement Survey tool here!
    const surveyToolId = '88f9310f-6ee6-49db-8024-ede8ecfeea85';
    navigate(`${prefix}/tools/${surveyToolId}/results?surveyTemplateId=${templateId}`);
  };

  // Filter templates based on search and category
  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) || [];

  // Get unique categories for filter
  const categories = [...new Set(templates?.map(t => t.category) || [])].sort();

  const renderTemplateList = () => {
    if (isLoading) {
      return (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-48" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="h-64">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-1/2 mb-2" />
                  <Skeleton className="h-8 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Failed to load survey templates</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="mt-4">
            Try Again
          </Button>
        </div>
      );
    }

    if (!templates || templates.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-2">No survey templates available</p>
          <p className="text-sm text-muted-foreground">Check back later for new templates</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category} className="capitalize">
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-2">No templates match your search</p>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setCategoryFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow h-fit">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        {template.name}
                        {template.is_featured && (
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        )}
                      </CardTitle>
                      <CardDescription className="mt-2 line-clamp-2">
                        {template.description}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {template.estimated_duration_minutes && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {template.estimated_duration_minutes}min
                      </div>
                    )}
                    {template.target_audience && (
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span className="truncate">{template.target_audience}</span>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="secondary" className="capitalize">
                      {template.category}
                    </Badge>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewTemplate(template)}
                      >
                        View details
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => handleUseTemplate(template.id)}
                        className="bg-gradient-primary text-white"
                      >
                        Use template
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <AppLayout>
      {/* SEO Meta Tags for Survey Templates List */}
      {!selectedTemplateId && (
        <SurveyTemplateListSEO 
          templatesCount={templates?.length} 
          categories={categories} 
        />
      )}
      <AppHeader
        title={selectedTemplate ? selectedTemplate.name : 'Survey Templates'}
        subtitle={
          selectedTemplate
            ? (selectedTemplate.description
                ? truncateWords(selectedTemplate.description, SUBTITLE_WORD_LIMIT)
                : 'Review the template details and questions below')
            : 'Browse our collection of pre-designed survey templates for various use cases'
        }
        breadcrumbs={
          selectedTemplate
            ? [
                { label: 'Survey Templates', href: `${prefix}/survey-templates` },
                { label: selectedTemplate.name }
              ]
            : [
                { label: 'Survey Templates' }
              ]
        }
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {selectedTemplateId ? (
            <Card className="shadow-elegant bg-white/95 backdrop-blur-sm">
              <CardContent className="p-8">
                <SurveyTemplateViewer
                  templateId={selectedTemplateId}
                  onBack={handleBack}
                  onUseTemplate={handleUseTemplate}
                />
              </CardContent>
            </Card>
          ) : (
            renderTemplateList()
          )}
        </div>
      </div>
      
      <Footer />
    </AppLayout>
  );
};

export default SurveyTemplates;